"use client";

import type { ReactNode } from "react";
import { HIGHLIGHT_TONES } from "@/components/tool-kit/highlighted-text";
import { CopyButton, Field } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  TEXTAREA_PROSE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  draftToHtml,
  draftToPlain,
  type Segment,
  segmentsOf,
  toPlain,
} from "@/lib/tool-kit/correspondence/compose";
import { downloadText } from "@/lib/tool-kit/download";

/*
 * What every academic-correspondence widget shares. On the form side: a compact text
 * area, a heading with "Try an example" and "Clear", and a titled card of fields. On
 * the output side: the draft drawn with its blanks highlighted, the copy and download
 * buttons, and a list of copyable alternatives. Logic lives in
 * `@/lib/tool-kit/correspondence`; every word on these controls comes in through props
 * from the calling widget's own strings.
 */

/**
 * The kit's prose text area without its 180px floor, so `rows` sets the height. A
 * reviewer comment or a list of three names is a few lines, and a column of 180px
 * boxes turns a form of twenty comments into a very long scroll. Still resizable.
 */
const COMPACT_TEXTAREA = TEXTAREA_PROSE.replace("min-h-[180px] ", "");

export function CompactTextArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  rows = 3,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  rows?: number;
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <textarea
        className={COMPACT_TEXTAREA}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        value={value}
      />
    </Field>
  );
}

/** Select or pill options from a list of keys and their labels. */
export function optionsOf<K extends string>(
  keys: readonly K[],
  labels: Readonly<Record<K, string>>
): { value: K; label: string }[] {
  return keys.map((value) => ({ value, label: labels[value] }));
}

/** The form's heading with its "Try an example" and "Clear" buttons. */
export function FormHeader({
  heading,
  exampleLabel,
  clearLabel,
  onExample,
  onClear,
}: {
  heading: string;
  exampleLabel: string;
  clearLabel: string;
  onExample: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className={WIDGET_H2}>{heading}</h2>
      <div className="flex flex-wrap gap-2">
        <button className={BUTTON_SECONDARY} onClick={onExample} type="button">
          {exampleLabel}
        </button>
        <button className={BUTTON_SECONDARY} onClick={onClear} type="button">
          {clearLabel}
        </button>
      </div>
    </div>
  );
}

/** A card of related form fields under a visible heading. */
export function FieldGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      {children}
    </div>
  );
}

export interface DraftStrings {
  /** Copies rich text (Word, Google Docs, email) with a plain-text twin. */
  copy: string;
  copyPlain: string;
  copied: string;
  download: string;
}

function SegmentRun({ segment }: { segment: Segment }) {
  let node: ReactNode = segment.text;
  if (segment.placeholder) {
    node = <mark className={HIGHLIGHT_TONES.amber}>[{node}]</mark>;
  }
  if (segment.italic) {
    node = <i>{node}</i>;
  }
  if (segment.bold) {
    node = <strong className="font-semibold">{node}</strong>;
  }
  return <>{node}</>;
}

/** One paragraph with its marks drawn: blanks highlighted, italics, bold labels. */
export function MarkedText({ text }: { text: string }) {
  return (
    <>
      {segmentsOf(text).map((segment, index) => (
        // Runs are positional pieces of one sentence.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional runs
        <SegmentRun key={index} segment={segment} />
      ))}
    </>
  );
}

/** The draft itself, in the tinted result card. */
export function DraftView({ paragraphs }: { paragraphs: readonly string[] }) {
  return (
    <div
      className={`${RESULT_CARD} max-h-[640px] space-y-3 overflow-y-auto text-foreground text-sm leading-relaxed`}
    >
      {paragraphs.map((paragraph, index) => (
        // Paragraphs are positional: the draft is rebuilt on every keystroke.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional paragraphs
        <p className="whitespace-pre-wrap break-words" key={index}>
          <MarkedText text={paragraph} />
        </p>
      ))}
    </div>
  );
}

/**
 * Heading, actions, a live status line and the draft.
 *
 * The status line — "2 placeholders left to fill" — is the live region, not the draft:
 * the draft is rebuilt on every keystroke, and announcing a whole letter each time
 * would drown a screen reader.
 */
export function DraftPanel({
  heading,
  paragraphs,
  status,
  filename,
  download,
  strings,
  intro,
  children,
}: {
  heading: string;
  paragraphs: readonly string[];
  status: string;
  filename: string;
  /** The .txt content, when it differs from the draft (an email's subject line). */
  download?: string;
  strings: DraftStrings;
  /** Shown between the status line and the draft — an email's subject lines. */
  intro?: ReactNode;
  children?: ReactNode;
}) {
  const plain = draftToPlain(paragraphs);
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={WIDGET_H2}>{heading}</h2>
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={strings.copied}
            html={draftToHtml(paragraphs)}
            label={strings.copy}
            text={plain}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyPlain}
            text={plain}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={plain === ""}
            onClick={() => downloadText(filename, download ?? plain)}
            type="button"
          >
            {strings.download}
          </button>
        </div>
      </div>
      <p aria-live="polite" className={HINT}>
        {status}
      </p>
      {intro}
      <DraftView paragraphs={paragraphs} />
      {children}
    </section>
  );
}

/** A short list of copyable lines — subject lines, other ways to phrase a request. */
export function CopyList({
  heading,
  items,
  copyLabel,
  copiedLabel,
}: {
  heading: string;
  items: readonly string[];
  copyLabel: string;
  copiedLabel: string;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li
            className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 text-foreground text-sm sm:flex-row sm:items-center sm:justify-between"
            key={item}
          >
            <span className="break-words">
              <MarkedText text={item} />
            </span>
            <span className="shrink-0">
              <CopyButton
                copiedLabel={copiedLabel}
                label={copyLabel}
                small
                text={toPlain(item)}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
